import { FC } from 'react'
import { cn } from '@duck/ui'
import { TableCellsIcon } from '@heroicons/react/24/outline'
import { KeyRound, ArrowRight } from 'lucide-react'
import { useAtomValue } from 'jotai'
import { getTableDataFromQuery } from './utils'
import { useEventEmitter } from '@/modules/EventBusModule'
import { useTar } from '@/modules/i18n'
import { workspaceTableMetadataAtom } from '@/store/workspaceAtoms'

interface SchemaLedgerProps {
  tableQueries: string[];
}

type FieldKind = 'pk' | 'fk' | 'col';

const getFieldKind = (type: string): FieldKind => {
  const upper = type?.toUpperCase() ?? ''
  if (upper.includes('PRIMARY KEY')) return 'pk'
  if (upper.includes('REFERENCES')) return 'fk'
  return 'col'
}

const parseFKRef = (type: string): string | null => {
  const match = /REFERENCES\s+"?(\w+)"?\s*(?:\(\s*"?(\w+)"?\s*\))?/i.exec(type ?? '')
  if (!match) return null
  return match[2] ? `${match[1]}.${match[2]}` : match[1]
}

const extractFlags = (type: string): string[] => {
  const upper = type?.toUpperCase() ?? ''
  const flags: string[] = []
  if (/\bUNIQUE\b/.test(upper)) flags.push('UNQ')
  if (/\bNOT\s+NULL\b/.test(upper)) flags.push('NN')
  if (/\bDEFAULT\b/.test(upper)) flags.push('DEF')
  return flags
}

const cleanType = (type: string): string => {
  return (type ?? '')
    .replace(/\s*PRIMARY\s+KEY\b/i, '')
    .replace(/\s*REFERENCES\s+"?\w+"?\s*(?:\([^)]+\))?/i, '')
    .replace(/\s*UNIQUE\b/i, '')
    .replace(/\s*NOT\s+NULL\b/i, '')
    .replace(/\s*DEFAULT\s+\S+/i, '')
    .trim()
}

export const SchemaLedger: FC<SchemaLedgerProps> = ({ tableQueries }) => {
  const { t } = useTar()
  const emitInsert = useEventEmitter('codeInput:insert')
  const tableMetadata = useAtomValue(workspaceTableMetadataAtom)

  return (
    <div className="space-y-2.5">
      {tableQueries.map((query, tableIdx) => {
        const { name, fields } = getTableDataFromQuery(query)

        return (
          <section
            key={tableIdx}
            dir="ltr"
            className="relative border border-border bg-card/40"
          >
            {/* Registration corner marks (blueprint style) */}
            <span aria-hidden className="pointer-events-none absolute -top-px -left-px w-2.5 h-2.5 border-t border-l border-primary/50 z-10" />
            <span aria-hidden className="pointer-events-none absolute -top-px -right-px w-2.5 h-2.5 border-t border-r border-primary/50 z-10" />
            <span aria-hidden className="pointer-events-none absolute -bottom-px -left-px w-2.5 h-2.5 border-b border-l border-primary/50 z-10" />
            <span aria-hidden className="pointer-events-none absolute -bottom-px -right-px w-2.5 h-2.5 border-b border-r border-primary/50 z-10" />
            <header
              className="grid grid-cols-[minmax(0,max-content)_minmax(0,1fr)] items-center gap-2 px-2 py-1 border-b border-dashed border-border bg-secondary/20"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, transparent 0 6px, hsl(var(--color-primary) / 0.05) 6px 7px)',
              }}
            >
              <button
                type="button"
                onClick={() => emitInsert({ value: `${name} ` })}
                className="flex items-center gap-2 min-w-0 px-1.5 py-0.5 text-sm font-medium text-foreground hover:bg-secondary/40 rounded-sm transition-colors"
              >
                <TableCellsIcon className="w-4 h-4 text-primary shrink-0" />
                <span className="truncate">{name}</span>
              </button>
              <span dir="auto" className="text-[10px] tracking-wider uppercase text-muted-foreground tabular-nums truncate text-end pe-1">
                {fields.length === 1
                  ? t('challenges.schemaColCount', { count: fields.length })
                  : t('challenges.schemaColsCount', { count: fields.length })}
                {tableMetadata?.[name] && (
                  <>
                    {' · '}
                    {tableMetadata[name].rowCount === 1
                      ? t('challenges.schemaRowCount', {
                          amount: tableMetadata[name].rowCount.toLocaleString(),
                        })
                      : t('challenges.schemaRowsCount', {
                          amount: tableMetadata[name].rowCount.toLocaleString(),
                        })}
                  </>
                )}
              </span>
            </header>

            <ul className="py-0.5">
              {fields.map((field, fieldIdx) => {
                const kind = getFieldKind(field.type)
                const ref = parseFKRef(field.type)
                const flags = extractFlags(field.type)
                const type = cleanType(field.type)

                return (
                  <li
                    key={`${field.name}-${fieldIdx}`}
                    className={cn(
                      'grid grid-cols-[20px_minmax(0,max-content)_minmax(0,1fr)] gap-2 items-center px-2 py-1 text-xs',
                      'border-b border-border/40 last:border-b-0',
                      'hover:bg-secondary/20 transition-colors'
                    )}
                  >
                    <span className="flex justify-center items-center">
                      {kind === 'pk' ? (
                        <KeyRound
                          className="w-3.5 h-3.5 text-primary"
                          aria-label={t('challenges.schemaPrimaryKey')}
                        />
                      ) : kind === 'fk' ? (
                        <ArrowRight
                          className="w-3.5 h-3.5 text-warning"
                          aria-label={t('challenges.schemaForeignKey')}
                        />
                      ) : (
                        <span
                          aria-hidden
                          className="text-muted-foreground/40 leading-none"
                        >
                          ·
                        </span>
                      )}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        emitInsert({ value: `${name}.${field.name} ` })
                      }
                      className="flex items-baseline gap-2 min-w-0 px-1 py-0 h-auto text-left"
                    >
                      <span
                        className={cn(
                          'font-mono text-[12.5px] truncate',
                          kind === 'pk' && 'text-primary',
                          kind === 'fk' && 'text-warning',
                          kind === 'col' && 'text-foreground'
                        )}
                      >
                        {field.name}
                      </span>
                      {ref && (
                        <span className="text-[9px] tracking-wider uppercase text-warning/70 whitespace-nowrap shrink-0">
                          ↗ {ref}
                        </span>
                      )}
                    </button>

                    <span className="flex items-center justify-end gap-1 text-[10px] text-muted-foreground min-w-0 overflow-hidden">
                      {type && (
                        <span className="font-mono tabular-nums truncate min-w-0">{type}</span>
                      )}
                      {flags.map((f) => (
                        <span
                          key={f}
                          className="px-1 border border-border rounded-sm text-muted-foreground/80 tracking-wider shrink-0"
                        >
                          {f}
                        </span>
                      ))}
                    </span>
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
